import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabaseClient";

/* ─── Types ─────────────────────────────────────────────── */
type ClientOption = { id: string; full_name: string; email: string };

type Template = {
  id: string;
  name: string;
  language: string;
  content: string;
  is_active: boolean;
};

type FormData = {
  clientId:      string;
  templateId:    string;
  title:         string;
  eventDate:     string;
  eventLocation: string;
  packageName:   string;
  totalAmount:   string;
  depositAmount: string;
};

type SavePayload = {
  template_id:      string;
  rendered_content: string;
  client_id:        string;
  client_name:      string;
  title:            string;
  event_date:       string;
  event_location:   string;
  package_name:     string;
  total_amount:     number;
  deposit_amount:   number;
  balance_amount:   number;
  contract_number:  string;
  status:           string;
};

interface Props {
  tenantId:   string;
  tenantName: string;
  clients:    ClientOption[];
  saving:     boolean;
  onClose:    () => void;
  onSave:     (payload: SavePayload) => void;
}

/* ─── Helpers ────────────────────────────────────────────── */
function buildVarMap(
  form: FormData,
  clients: ClientOption[],
  tenantName: string,
): Record<string, string> {
  const client  = clients.find((c) => c.id === form.clientId);
  const total   = parseFloat(form.totalAmount || "0");
  const deposit = parseFloat(form.depositAmount || "0");
  const balance = Math.max(0, total - deposit);
  const today   = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return {
    // Core variables (as requested)
    client_name:       client?.full_name ?? "",
    business_name:     tenantName,
    contract_total:    total > 0 ? `$${total.toLocaleString()}` : "",
    deposit:           deposit > 0 ? `$${deposit.toLocaleString()}` : "",
    balance:           `$${balance.toLocaleString()}`,
    event_date:        form.eventDate
      ? new Date(form.eventDate + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : "",
    event_type:        form.packageName,
    // Aliases kept for backward-compat
    studio_name:       tenantName,
    photographer_name: tenantName,
    event_location:    form.eventLocation,
    location:          form.eventLocation,
    total_amount:      total > 0 ? `$${total.toLocaleString()}` : "",
    deposit_amount:    deposit > 0 ? `$${deposit.toLocaleString()}` : "",
    balance_amount:    `$${balance.toLocaleString()}`,
    today_date:        today,
    delivery_weeks:    "2",
    package_name:      form.packageName,
  };
}

function renderContent(
  raw: string,
  varMap: Record<string, string>,
): { html: string; plainText: string; pending: string[] } {
  const pending: string[] = [];

  const html = raw.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
    const val = varMap[key];
    if (val) return `<span class="var-filled">${val}</span>`;
    pending.push(key);
    return `<span class="var-pending">{{${key}}}</span>`;
  });

  const plainText = raw.replace(/\{\{(\w+)\}\}/g, (_match, key) => varMap[key] ?? `{{${key}}}`);

  return { html, plainText, pending: [...new Set(pending)] };
}

function generateContractNumber(): string {
  const d    = new Date();
  const date = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const rand = String(Math.floor(Math.random() * 900) + 100);
  return `CT-${date}-${rand}`;
}

const LANG_LABELS: Record<string, string> = {
  en: "EN", es: "ES", pt: "PT", fr: "FR", de: "DE", it: "IT",
};

const INPUT = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors";

/* ─── Component ──────────────────────────────────────────── */
export default function FromTemplateModal({
  tenantId, tenantName, clients, saving, onClose, onSave,
}: Props) {
  const [templates, setTemplates]   = useState<Template[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [step, setStep]             = useState<1 | 2>(1);

  const [form, setForm] = useState<FormData>({
    clientId:      "",
    templateId:    "",
    title:         "",
    eventDate:     "",
    eventLocation: "",
    packageName:   "",
    totalAmount:   "",
    depositAmount: "",
  });

  /* Load active templates */
  useEffect(() => {
    const load = async () => {
      setLoadingTpl(true);
      const { data } = await supabase
        .from("contract_templates")
        .select("id, name, language, content, is_active")
        .eq("tenant_id", tenantId)
        .eq("is_active", true)
        .order("name");
      if (data) setTemplates(data as Template[]);
      setLoadingTpl(false);
    };
    load();
  }, [tenantId]);

  /* Auto-fill title when template selected */
  useEffect(() => {
    const tpl = templates.find((t) => t.id === form.templateId);
    if (tpl && !form.title) setForm((p) => ({ ...p, title: tpl.name }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.templateId, templates]);

  const set = (k: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const selectedTpl    = templates.find((t) => t.id === form.templateId);
  const selectedClient = clients.find((c) => c.id === form.clientId);
  const total          = parseFloat(form.totalAmount || "0");
  const deposit        = parseFloat(form.depositAmount || "0");
  const balance        = Math.max(0, total - deposit);

  const varMap   = useMemo(() => buildVarMap(form, clients, tenantName), [form, clients, tenantName]);
  const rendered = useMemo(
    () => selectedTpl ? renderContent(selectedTpl.content, varMap) : { html: "", plainText: "", pending: [] },
    [selectedTpl, varMap],
  );

  const step1Valid = !!form.clientId && !!form.templateId;
  const step2Valid = !!form.title && total > 0;

  const handleSubmit = () => {
    if (!selectedTpl || !selectedClient) return;
    onSave({
      template_id:      selectedTpl.id,
      rendered_content: rendered.plainText,
      client_id:        selectedClient.id,
      client_name:      selectedClient.full_name,
      title:            form.title,
      event_date:       form.eventDate,
      event_location:   form.eventLocation,
      package_name:     form.packageName,
      total_amount:     total,
      deposit_amount:   deposit,
      balance_amount:   balance,
      contract_number:  generateContractNumber(),
      status:           "draft",
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 shadow-xl rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#C8A96E]/10 rounded-lg">
              <i className="ri-file-copy-2-line text-[#C8A96E]"></i>
            </div>
            <div>
              <h2 className="text-[#0f172a] font-semibold text-base">Create Contract from Template</h2>
              <p className="text-slate-400 text-xs">Fill in details and preview before creating</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Step indicator */}
            <div className="flex items-center gap-2 mr-2">
              <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 1 ? "text-[#C8A96E]" : "text-emerald-600"}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === 1 ? "bg-[#C8A96E] text-white" : "bg-emerald-100 text-emerald-600"}`}>
                  {step === 1 ? "1" : <i className="ri-check-line"></i>}
                </div>
                Select
              </div>
              <div className="w-6 h-px bg-slate-200"></div>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 2 ? "text-[#C8A96E]" : "text-slate-400"}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === 2 ? "bg-[#C8A96E] text-white" : "bg-slate-100 text-slate-400"}`}>
                  2
                </div>
                Details &amp; Preview
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">

          {/* ── STEP 1: Select client + template ── */}
          {step === 1 && (
            <div className="flex-1 flex flex-col p-6 overflow-y-auto gap-6">
              <p className="text-slate-500 text-sm">Choose the client and the template to base the contract on.</p>

              {/* Client */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                  Client *
                </label>
                {clients.length === 0 ? (
                  <p className="text-slate-400 text-sm">No active clients found.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1">
                    {clients.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setForm((p) => ({ ...p, clientId: c.id }))}
                        className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                          form.clientId === c.id
                            ? "bg-[#C8A96E]/5 border-[#C8A96E]/40"
                            : "bg-white border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
                          form.clientId === c.id ? "bg-[#C8A96E]/15" : "bg-slate-100"
                        }`}>
                          <i className={`ri-user-line text-sm ${form.clientId === c.id ? "text-[#C8A96E]" : "text-slate-400"}`}></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium truncate ${form.clientId === c.id ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>
                            {c.full_name}
                          </p>
                          <p className="text-slate-400 text-xs truncate">{c.email}</p>
                        </div>
                        {form.clientId === c.id && (
                          <i className="ri-check-line text-[#C8A96E] flex-shrink-0"></i>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Template */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                  Template *
                </label>
                {loadingTpl ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Loading templates…
                  </div>
                ) : templates.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
                    <i className="ri-information-line mr-1.5"></i>
                    No active templates found. Create one in{" "}
                    <a href="/admin/contracts/templates" className="underline font-medium">
                      Contracts → Templates
                    </a>.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-1">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setForm((p) => ({ ...p, templateId: t.id }))}
                        className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                          form.templateId === t.id
                            ? "bg-[#C8A96E]/5 border-[#C8A96E]/40"
                            : "bg-white border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                          form.templateId === t.id ? "bg-[#C8A96E]/10" : "bg-slate-100"
                        }`}>
                          <i className={`ri-file-text-line text-base ${form.templateId === t.id ? "text-[#C8A96E]" : "text-slate-400"}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${form.templateId === t.id ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>
                            {t.name}
                          </p>
                          <p className="text-slate-400 text-xs mt-0.5">
                            {t.content.length.toLocaleString()} chars ·{" "}
                            {[...new Set(t.content.match(/\{\{[^}]+\}\}/g) ?? [])].length} variables
                          </p>
                        </div>
                        <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider flex-shrink-0">
                          {LANG_LABELS[t.language] ?? t.language}
                        </span>
                        {form.templateId === t.id && (
                          <i className="ri-check-line text-[#C8A96E] flex-shrink-0"></i>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setStep(2)}
                  disabled={!step1Valid}
                  className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Next: Fill Details
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Form + Preview ── */}
          {step === 2 && (
            <>
              {/* Left — Form */}
              <div className="w-[340px] flex-shrink-0 border-r border-slate-200 overflow-y-auto p-6 flex flex-col gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1.5 text-slate-400 hover:text-[#0f172a] text-xs transition-colors cursor-pointer w-fit"
                >
                  <i className="ri-arrow-left-line"></i> Back
                </button>

                {/* Selected summary */}
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line text-slate-400 text-sm"></i>
                    <span className="text-[#0f172a] text-sm font-medium">{selectedClient?.full_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-file-text-line text-slate-400 text-sm"></i>
                    <span className="text-slate-600 text-xs">{selectedTpl?.name}</span>
                    <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">
                      {LANG_LABELS[selectedTpl?.language ?? "en"]}
                    </span>
                  </div>
                </div>

                {/* Contract title */}
                <div>
                  <label className="block text-slate-500 text-xs font-semibold mb-1.5 uppercase tracking-wider">Contract Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={set("title")}
                    placeholder="e.g. Wedding Photography Contract"
                    className={INPUT}
                  />
                </div>

                {/* Event date */}
                <div>
                  <label className="block text-slate-500 text-xs font-semibold mb-1.5 uppercase tracking-wider">Event Date</label>
                  <input type="date" value={form.eventDate} onChange={set("eventDate")} className={INPUT} />
                </div>

                {/* Event location */}
                <div>
                  <label className="block text-slate-500 text-xs font-semibold mb-1.5 uppercase tracking-wider">Event Location</label>
                  <input
                    type="text"
                    value={form.eventLocation}
                    onChange={set("eventLocation")}
                    placeholder="Grand Hotel, Miami FL"
                    className={INPUT}
                  />
                </div>

                {/* Package */}
                <div>
                  <label className="block text-slate-500 text-xs font-semibold mb-1.5 uppercase tracking-wider">Package / Event Type</label>
                  <input
                    type="text"
                    value={form.packageName}
                    onChange={set("packageName")}
                    placeholder="Full Day Wedding Coverage"
                    className={INPUT}
                  />
                </div>

                {/* Financial */}
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-slate-500 text-xs uppercase tracking-wider mb-3 font-semibold">Financial</p>
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="block text-slate-500 text-xs font-semibold mb-1.5">Total Amount *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          value={form.totalAmount}
                          onChange={set("totalAmount")}
                          placeholder="0.00"
                          className="w-full bg-white border border-slate-200 rounded-lg pl-6 pr-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-500 text-xs font-semibold mb-1.5">Deposit</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          value={form.depositAmount}
                          onChange={set("depositAmount")}
                          placeholder="0.00"
                          className="w-full bg-white border border-slate-200 rounded-lg pl-6 pr-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
                        />
                      </div>
                    </div>
                    {/* Balance preview */}
                    <div className={`rounded-lg px-3 py-2.5 flex items-center justify-between border ${
                      balance > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
                    }`}>
                      <span className="text-slate-500 text-xs font-medium">Balance</span>
                      <span className={`text-sm font-bold ${balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                        ${balance.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pending variables warning */}
                {rendered.pending.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-amber-700 text-xs font-semibold mb-1.5 flex items-center gap-1.5">
                      <i className="ri-alert-line"></i>
                      {rendered.pending.length} unfilled variable{rendered.pending.length !== 1 ? "s" : ""}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {rendered.pending.map((v) => (
                        <span key={v} className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded font-mono">
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                    <p className="text-amber-600/70 text-[10px] mt-1.5">These will remain as-is in the saved contract.</p>
                  </div>
                )}

                {rendered.pending.length === 0 && total > 0 && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
                    <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>
                    <p className="text-emerald-700 text-xs font-medium">All variables filled — ready to create!</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 mt-auto">
                  <button
                    onClick={onClose}
                    className="flex-1 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!step2Valid || saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {saving ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Creating…</>
                    ) : (
                      <><i className="ri-save-3-line"></i> Create Contract</>
                    )}
                  </button>
                </div>
              </div>

              {/* Right — Live Preview */}
              <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    Live Preview
                  </p>
                  <div className="flex items-center gap-3">
                    {rendered.pending.length > 0 ? (
                      <span className="text-amber-600 text-xs flex items-center gap-1">
                        <i className="ri-alert-line text-xs"></i>
                        {rendered.pending.length} pending
                      </span>
                    ) : total > 0 ? (
                      <span className="text-emerald-600 text-xs flex items-center gap-1">
                        <i className="ri-check-line text-xs"></i>
                        All filled
                      </span>
                    ) : null}
                    <span className="bg-slate-200 text-slate-500 text-xs px-2.5 py-1 rounded-md">Draft</span>
                  </div>
                </div>

                {selectedTpl ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-6 min-h-[400px]">
                    {/* Contract header */}
                    <div className="border-b border-slate-100 pb-4 mb-5">
                      <h3 className="text-[#0f172a] font-bold text-base">{form.title || selectedTpl.name}</h3>
                      {selectedClient && (
                        <p className="text-slate-500 text-sm mt-0.5">{selectedClient.full_name}</p>
                      )}
                    </div>
                    {/* Rendered content */}
                    <div
                      className="text-[#0f172a]/80 text-sm leading-relaxed whitespace-pre-wrap rendered-preview"
                      dangerouslySetInnerHTML={{ __html: rendered.html }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
                    Select a template to see preview
                  </div>
                )}

                {/* Variable legend */}
                <div className="mt-4 flex items-center gap-4 text-xs flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-0.5 rounded bg-[#C8A96E]/12 text-[#C8A96E] font-mono text-[10px]">value</span>
                    <span className="text-slate-400">Variable replaced</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-mono text-[10px]">{`{{var}}`}</span>
                    <span className="text-slate-400">Still pending</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .rendered-preview .var-filled {
          background: rgba(200,169,110,.12);
          color: #9a7840;
          padding: 0 3px;
          border-radius: 3px;
          font-weight: 500;
        }
        .rendered-preview .var-pending {
          background: rgba(251,191,36,.12);
          color: #d97706;
          padding: 0 3px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}